import { FormEvent, useMemo, useState } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { t } from '@/shared/lib/i18n'
import type WebNotificationPreferencesPage from '@/shared/types/web/WebNotificationPreferencesPage'
import {
  type GuidedFormState,
  hydrateFormFromPage,
  expandFormToPayload,
  detectMixedDelivery,
  detectMixedScope,
  projectedPreferenceRowCount,
  MAX_PREFERENCE_ROWS,
} from './formModel'

interface FormErrors {
  base?: string | string[]
}

// The self-service page and the admin "edit another user" page share this form. It is
// parameterized by the target user's page data, the PATCH path, and whether the actor may save,
// so both entry points feed the same Notifications::PreferencePageSaveService payload.
export interface PreferenceFormProps {
  notificationPreferencesPage: WebNotificationPreferencesPage
  patchPath: string
  canSubmit: boolean
}

const FREQUENCY_BANDS = ['immediate', 'daily', 'weekly'] as const

const StepCard = ({
  number,
  heading,
  helper,
  children,
  error,
}: {
  number: number
  heading: string
  helper: string
  children: React.ReactNode
  error?: string
}) => (
  <section className="space-y-3 rounded-lg border border-border bg-white p-5">
    <div className="space-y-1">
      <h2 className="text-base font-semibold">
        <span className="mr-2 text-muted-foreground">{number}.</span>
        {heading}
      </h2>
      <p className="text-sm text-muted-foreground">{helper}</p>
    </div>
    {children}
    {error && <p className="text-sm font-medium text-destructive">{error}</p>}
  </section>
)

const PreferenceForm = ({
  notificationPreferencesPage,
  patchPath,
  canSubmit,
}: PreferenceFormProps) => {
  const initialState = useMemo(
    () => hydrateFormFromPage(notificationPreferencesPage),
    [notificationPreferencesPage],
  )

  const hasMixedDelivery = useMemo(
    () => detectMixedDelivery(notificationPreferencesPage),
    [notificationPreferencesPage],
  )

  const hasMixedScope = useMemo(
    () => detectMixedScope(notificationPreferencesPage),
    [notificationPreferencesPage],
  )

  const [formState, setFormState] = useState<GuidedFormState>(initialState)
  const [droppedDepartmentCount, setDroppedDepartmentCount] = useState(0)
  const form = useForm({})

  // REBUILD-448 option 2: Daily/Weekly SMS is supported — surface that when both are selected.
  const showsDigestSmsNote = useMemo(() => {
    if (!formState.deliverBySms) return false
    return formState.selectedFrequencies.some((band) => band === 'daily' || band === 'weekly')
  }, [formState.deliverBySms, formState.selectedFrequencies])

  const baseErrorText = useMemo(() => {
    const baseErrors = (form.errors as FormErrors).base
    if (typeof baseErrors === 'string') return baseErrors
    if (Array.isArray(baseErrors)) return baseErrors.filter(Boolean).join(' ')
    return ''
  }, [form.errors])

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const filteredDepartments = useMemo(() => {
    if (formState.allSites) return notificationPreferencesPage.departments
    if (formState.selectedSiteIds.length === 0) return notificationPreferencesPage.departments
    return notificationPreferencesPage.departments.filter(
      (department) =>
        department.siteId != null && formState.selectedSiteIds.includes(department.siteId),
    )
  }, [notificationPreferencesPage.departments, formState.allSites, formState.selectedSiteIds])

  const updateForm = (partial: Partial<GuidedFormState>) => {
    const willConstrainDepartments =
      (partial.selectedSiteIds !== undefined || partial.allSites !== undefined) &&
      !(partial.allSites ?? formState.allSites) &&
      !(partial.allDepartments ?? formState.allDepartments)

    let droppedCount = 0
    if (willConstrainDepartments) {
      const nextSiteIds = partial.selectedSiteIds ?? formState.selectedSiteIds
      const allowedSiteIds = new Set(nextSiteIds)
      const currentDepartmentIds = partial.selectedDepartmentIds ?? formState.selectedDepartmentIds
      const keptCount = currentDepartmentIds.filter((departmentId) => {
        const department = notificationPreferencesPage.departments.find(
          (d) => d.id === departmentId,
        )
        return department?.siteId == null || allowedSiteIds.has(department.siteId)
      }).length
      droppedCount = currentDepartmentIds.length - keptCount
    }

    setFormState((previous) => {
      const next = { ...previous, ...partial }

      if (partial.allSites !== undefined && partial.allSites) {
        next.selectedSiteIds = []
      }
      if (partial.allDepartments !== undefined && partial.allDepartments) {
        next.selectedDepartmentIds = []
      }

      if (partial.selectedSiteIds !== undefined || partial.allSites !== undefined) {
        if (!next.allSites && !next.allDepartments) {
          const allowedSiteIds = new Set(next.selectedSiteIds)
          next.selectedDepartmentIds = next.selectedDepartmentIds.filter((departmentId) => {
            const department = notificationPreferencesPage.departments.find(
              (d) => d.id === departmentId,
            )
            return department?.siteId == null || allowedSiteIds.has(department.siteId)
          })
        }
      }

      return next
    })
    setDroppedDepartmentCount(droppedCount)
    setValidationErrors({})
  }

  const toggleSite = (siteId: number, checked: boolean) => {
    const next = checked
      ? [...formState.selectedSiteIds, siteId]
      : formState.selectedSiteIds.filter((id) => id !== siteId)
    updateForm({ selectedSiteIds: next, allSites: false })
  }

  const toggleDepartment = (departmentId: number, checked: boolean) => {
    const next = checked
      ? [...formState.selectedDepartmentIds, departmentId]
      : formState.selectedDepartmentIds.filter((id) => id !== departmentId)
    updateForm({ selectedDepartmentIds: next, allDepartments: false })
  }

  const toggleEventType = (eventTypeId: number, checked: boolean) => {
    const next = checked
      ? [...formState.selectedEventTypeIds, eventTypeId]
      : formState.selectedEventTypeIds.filter((id) => id !== eventTypeId)
    updateForm({ selectedEventTypeIds: next })
  }

  const toggleFrequency = (band: (typeof FREQUENCY_BANDS)[number], checked: boolean) => {
    const next = checked
      ? [...formState.selectedFrequencies, band]
      : formState.selectedFrequencies.filter((f) => f !== band)
    updateForm({ selectedFrequencies: next })
  }

  const validate = (): boolean => {
    const errors: Record<string, string> = {}
    if (formState.selectedEventTypeIds.length === 0) {
      errors.eventTypes = t('notificationPreferences.errorNoEventTypes')
    }
    if (formState.selectedFrequencies.length === 0) {
      errors.frequency = t('notificationPreferences.errorNoFrequency')
    }
    if (!formState.deliverByEmail && !formState.deliverBySms) {
      errors.channels = t('notificationPreferences.errorNoChannel')
    }
    if (projectedPreferenceRowCount(formState) > MAX_PREFERENCE_ROWS) {
      errors.tooManyRows = t('notificationPreferences.errorTooManyRows', {
        max: MAX_PREFERENCE_ROWS,
      })
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!validate()) return

    const payload = expandFormToPayload(formState, notificationPreferencesPage)
    form.transform(() => ({ notification_preference: payload }))
    form.patch(patchPath)
  }

  return (
    <>
      {baseErrorText && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          role="alert"
        >
          {baseErrorText}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Step 1: Sites */}
        <StepCard
          number={1}
          heading={t('notificationPreferences.sitesHeading')}
          helper={t('notificationPreferences.sitesHelper')}
        >
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox
                checked={formState.allSites}
                disabled={!canSubmit}
                onCheckedChange={(checked) => updateForm({ allSites: Boolean(checked) })}
              />
              {t('notificationPreferences.allSites')}
            </label>
            {!formState.allSites && (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {notificationPreferencesPage.sites.map((site) => (
                  <label key={site.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={formState.selectedSiteIds.includes(site.id)}
                      disabled={!canSubmit}
                      onCheckedChange={(checked) => toggleSite(site.id, Boolean(checked))}
                    />
                    {site.name}
                  </label>
                ))}
              </div>
            )}
          </div>
        </StepCard>

        {/* Step 2: Departments */}
        <StepCard
          number={2}
          heading={t('notificationPreferences.departmentsHeading')}
          helper={t('notificationPreferences.departmentsHelper')}
        >
          <div className="space-y-3">
            {!formState.allSites && (
              <p className="text-xs text-muted-foreground italic">
                {t('notificationPreferences.departmentsScopedToSitesHint')}
              </p>
            )}
            {droppedDepartmentCount > 0 && (
              <p className="text-xs italic text-amber-600">
                {t('notificationPreferences.departmentDroppedBySiteHint')}
              </p>
            )}
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox
                checked={formState.allDepartments}
                disabled={!canSubmit}
                onCheckedChange={(checked) => updateForm({ allDepartments: Boolean(checked) })}
              />
              {t('notificationPreferences.allDepartments')}
            </label>
            {!formState.allDepartments && (
              <>
                {filteredDepartments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('notificationPreferences.departmentsEmpty')}
                  </p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {filteredDepartments.map((department) => (
                      <label key={department.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={formState.selectedDepartmentIds.includes(department.id)}
                          disabled={!canSubmit}
                          onCheckedChange={(checked) =>
                            toggleDepartment(department.id, Boolean(checked))
                          }
                        />
                        <span>
                          {department.siteName
                            ? `${department.name} — ${department.siteName}`
                            : department.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </StepCard>

        {/* Step 3: Event types */}
        <StepCard
          number={3}
          heading={t('notificationPreferences.eventTypesHeading')}
          helper={t('notificationPreferences.eventTypesHelper')}
          error={validationErrors.eventTypes}
        >
          <div className="space-y-2">
            {canSubmit && (
              <div className="flex items-center gap-3 text-xs">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() =>
                    updateForm({
                      selectedEventTypeIds: notificationPreferencesPage.notificationTypes.map(
                        (type) => type.id,
                      ),
                    })
                  }
                >
                  {t('notificationPreferences.selectAll')}
                </button>
                <span className="text-muted-foreground">·</span>
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => updateForm({ selectedEventTypeIds: [] })}
                >
                  {t('notificationPreferences.selectNone')}
                </button>
              </div>
            )}
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {notificationPreferencesPage.notificationTypes.map((notificationType) => (
                <label key={notificationType.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={formState.selectedEventTypeIds.includes(notificationType.id)}
                    disabled={!canSubmit}
                    onCheckedChange={(checked) =>
                      toggleEventType(notificationType.id, Boolean(checked))
                    }
                  />
                  <span>
                    {notificationType.name ?? notificationType.code ?? `#${notificationType.id}`}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </StepCard>

        {/* Step 4: When (frequency) */}
        <StepCard
          number={4}
          heading={t('notificationPreferences.whenHeading')}
          helper={t('notificationPreferences.whenHelper')}
          error={validationErrors.frequency}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            {FREQUENCY_BANDS.map((band) => (
              <label key={band} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={formState.selectedFrequencies.includes(band)}
                  disabled={!canSubmit}
                  onCheckedChange={(checked) => toggleFrequency(band, Boolean(checked))}
                />
                {t(`notificationPreferences.frequency.${band}`)}
              </label>
            ))}
          </div>
        </StepCard>

        {/* Step 5: Deliver by (channel) */}
        <StepCard
          number={5}
          heading={t('notificationPreferences.deliverByHeading')}
          helper={t('notificationPreferences.deliverByHelper')}
          error={validationErrors.channels}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={formState.deliverByEmail}
                  disabled={!canSubmit}
                  onCheckedChange={(checked) => updateForm({ deliverByEmail: Boolean(checked) })}
                />
                {t('notificationPreferences.channelEmail')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={formState.deliverBySms}
                  disabled={!canSubmit}
                  onCheckedChange={(checked) => updateForm({ deliverBySms: Boolean(checked) })}
                />
                {t('notificationPreferences.channelSms')}
              </label>
            </div>
            {showsDigestSmsNote && (
              <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                {t('notificationPreferences.digestSmsSupportedNote')}
              </p>
            )}
            {hasMixedDelivery && (
              <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                {t('notificationPreferences.mixedDeliveryLoadNote')}
              </p>
            )}
          </div>
        </StepCard>

        {/* Save */}
        <section className="space-y-4">
          {hasMixedScope && (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              {t('notificationPreferences.mixedScopeLoadNote')}
            </p>
          )}

          {validationErrors.tooManyRows && (
            <p className="text-sm font-medium text-destructive">{validationErrors.tooManyRows}</p>
          )}

          {!canSubmit && <NoAccessNotice />}

          {canSubmit && (
            <Button type="submit" disabled={form.processing}>
              {form.processing
                ? t('notificationPreferences.saving')
                : t('notificationPreferences.save')}
            </Button>
          )}
        </section>
      </form>
    </>
  )
}

export default PreferenceForm
